import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { CheckCircle2, AlertCircle, Save, Plug, Loader2, Trash2 } from "lucide-react";

// Phase 1 (B5): provider list is now fetched from the central registry
// (GET /api/admin/providers/registry). Adding a new provider is a one-row
// edit in backend/services/providers.py:KNOWN_PROVIDERS — the card appears
// here automatically. The legacy hardcoded PROVIDERS array was removed.

export default function KeysTab() {
  const [providers, setProviders] = useState([]);   // [{slug,name,blurb,placeholder,hint,configured,...}]
  const [providersLoaded, setProvidersLoaded] = useState(false);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});
  const [revoking, setRevoking] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/providers/registry");
      setProviders(data?.providers || []);
      setProvidersLoaded(true);
    } catch (e) {
      toast.error("Failed to load providers");
      setProvidersLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const payload = {};
    Object.entries(values).forEach(([k, v]) => { if (v && v.trim()) payload[k] = v.trim(); });
    if (Object.keys(payload).length === 0) return toast.error("Nothing to save");
    setSaving(true);
    try {
      await api.put("/admin/keys", payload);
      toast.success("Keys saved");
      setValues({});
      setTestResults({});
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testKey = async (provider) => {
    setTesting((t) => ({ ...t, [provider]: true }));
    setTestResults((r) => ({ ...r, [provider]: null }));
    try {
      const { data } = await api.post(`/admin/keys/test/${provider}`);
      setTestResults((r) => ({ ...r, [provider]: data }));
      if (data.ok) toast.success(`${provider}: ${data.message}`);
      else toast.error(`${provider}: ${data.message || "failed"}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setTestResults((r) => ({ ...r, [provider]: { ok: false, message: msg } }));
      toast.error(`${provider}: ${msg}`);
    } finally {
      setTesting((t) => ({ ...t, [provider]: false }));
    }
  };

  const revokeKey = async (provider) => {
    if (!window.confirm(`Revoke the stored ${provider} key? This cannot be undone — paste a new key after to re-enable.`)) return;
    setRevoking((r) => ({ ...r, [provider]: true }));
    try {
      // Empty string explicitly tells the backend to DELETE this provider's key.
      await api.put("/admin/keys", { [provider]: "" });
      toast.success(`${provider} key revoked`);
      setValues((v) => ({ ...v, [provider]: "" }));
      setTestResults((r) => ({ ...r, [provider]: null }));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Revoke failed");
    } finally {
      setRevoking((r) => ({ ...r, [provider]: false }));
    }
  };

  return (
    <div>
      {!providersLoaded && (
        <div className="p-8 text-sm text-muted-foreground font-mono flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading providers…
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
        {providers.map((p) => {
          const result = testResults[p.slug];
          return (
            <div key={p.slug} className="bg-card p-6" data-testid={`key-card-${p.slug}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold tracking-tight text-lg">{p.name}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{p.blurb}</div>
                </div>
                {p.configured ? (
                  <div className="flex items-center gap-1.5 text-green-600 text-xs font-mono">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>configured</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs font-mono">
                    <AlertCircle className="h-4 w-4" />
                    <span>missing</span>
                  </div>
                )}
              </div>
              {p.preview && (
                <div className="mt-3 text-xs font-mono text-muted-foreground">
                  Current: <span className="text-foreground">{p.preview}</span>
                </div>
              )}
              <div className="mt-4">
                <Input
                  type="password"
                  placeholder={p.placeholder}
                  value={values[p.slug] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [p.slug]: e.target.value }))}
                  className="font-mono rounded-sm h-10"
                  data-testid={`key-input-${p.slug}`}
                />
                <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">{p.hint}</div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm h-9"
                  disabled={!p.configured || testing[p.slug]}
                  onClick={() => testKey(p.slug)}
                  data-testid={`test-key-${p.slug}`}
                >
                  {testing[p.slug] ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plug className="h-3.5 w-3.5 mr-1.5" />}
                  Test connection
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm h-9 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 border-red-200 dark:border-red-900/40"
                  disabled={!p.configured || revoking[p.slug]}
                  onClick={() => revokeKey(p.slug)}
                  data-testid={`revoke-key-${p.slug}`}
                  title="Permanently delete the stored key (no undo)"
                >
                  {revoking[p.slug] ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
                  Revoke
                </Button>
                {result && (
                  <div className={`text-xs font-mono ${result.ok ? "text-green-600" : "text-destructive"} truncate`}>
                    {result.ok ? `✓ ${result.message}` : `✗ ${result.message}`}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button onClick={submit} disabled={saving} className="rounded-sm h-10" data-testid="save-keys-button">
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Saving…" : "Save updated keys"}
        </Button>
        <span className="text-xs text-muted-foreground font-mono">
          Keys are encrypted at rest (Fernet). Leave a field blank to keep the existing value.
        </span>
      </div>
    </div>
  );
}
